import { ICouponServiceProps } from "@/api_framework/api_modals/coupon";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import Input from "@/app/components/ui/Inputs/Input/index";
import InputWithFileUpload from "@/app/components/ui/Inputs/InputWithFileUpload";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useCouponCouponAllowEdit } from "@/app/hooks/api_hooks/coupon/useCouponCouponAllowEdit";
import { useCouponCouponTypeListAPI } from "@/app/hooks/api_hooks/coupon/useCouponCouponTypeListAPI";
import { useCouponCreationAPI } from "@/app/hooks/api_hooks/coupon/useCouponCreationAPI";
import { useCouponDetailsAPI } from "@/app/hooks/api_hooks/coupon/useCouponDetailsAPI";
import { useCouponUpdationAPI } from "@/app/hooks/api_hooks/coupon/useCouponUpdationAPI";
import { useImageUploadToS3API } from "@/app/hooks/api_hooks/coupon/useImageUploadToS3API";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { RxUpload } from "react-icons/rx";
import { useNavigate, useSearchParams } from "react-router-dom";

const booleanArray = [
  {
    id: "1",
    title: "YES",
  },
  {
    id: "2",
    title: "NO",
  },
];

export interface ICouponServiceDetailsFromFiled {
  coupon_id: string;
  page_step: string;
  coupon_name: string;
  coupon_code: string;
  is_membership_coupon?: string;
  is_exhaustible?: string;
  description: string | null;
  heading: string | null;
  coupon_type: string;
  icon_image: string;
  image?: FileList;
  terms_and_conditions: string | null;
  max_apply: string;
  max_value: string;
  start_date?: string;
  start_time_h?: string;
  start_time_m?: string;
  end_date?: string;
  end_time_h?: string;
  end_time_m?: string;
  start_date_time: number;
  end_date_time: number;
  created_at: number;
  updated_at: number;
  created_by: string;
  updated_by: string;
  coupon_expiry?: string;
  apply_changes?: string;
  min_cart_value: string;
}

const CouponDetailsComponent = () => {
  const { forOnlyPercentageAndNumberRegex, forAllAcceptWithOutStartWithDotAndSpace } = useFormValidations();
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [params, _setParams] = useSearchParams();
  const [couponTypeData, setCouponTypeData] = useState([]);
  const stringConstants = useStringConstants();
  const arrayConstants = useArrayConstants();
  const navigation = useNavigate();
  const coupon_id = params.get("coupon_id");

  const formHook = useForm<ICouponServiceDetailsFromFiled>({
    mode: "onChange",
    defaultValues: {
      max_apply: "YES",
      coupon_expiry: "YES",
      is_membership_coupon: "YES",
      is_exhaustible: "NO",
    },
  });

  const { execute: fetchCouponType, couponType } = useCouponCouponTypeListAPI();
  const { execute: fetchAllowCoupon, allowCoupon } = useCouponCouponAllowEdit();
  const { execute: convertFileToImage } = useImageUploadToS3API();
  const { execute: couponCreation } = useCouponCreationAPI();
  const { execute: updateCoupons } = useCouponUpdationAPI();
  const { execute: fetchCouponDetails, couponDetails } = useCouponDetailsAPI();
  const couponTypeItems = useSelectMenuReducer(couponType, "value", "id");
  const allowCouponItem = useSelectMenuReducer(allowCoupon, "label", "value");
  const startTimeItem = arrayConstants.overBookingStartTimeItems as ISelectMenuItemData[];
  const startTimeMinuteItem = arrayConstants.overBookingStartTimeMinuteItems as ISelectMenuItemData[];

  const RESTRICTED_COUPON_TYPES = ["DISCOUNT ON MEMBERSHIP", "QC COUPON"];
  const isMembershipDisabled = coupon_id ? true : RESTRICTED_COUPON_TYPES.includes(formHook.watch("coupon_type"));

  const isQCCoupon = formHook.watch("coupon_type") === "QC COUPON";

  useEffect(() => {
    if (isQCCoupon) {
      formHook.setValue("coupon_expiry", "YES");
    }
  }, [isQCCoupon]);

  const onSubmit = (data: ICouponServiceDetailsFromFiled) => {
    if (isQCCoupon && !data.icon_image) {
      toast.error("Icon Image is required for QC COUPON");
      return null;
    }
    if (isQCCoupon && !data.terms_and_conditions?.trim()) {
      toast.error("Terms & Conditions is required for QC COUPON");
      return null;
    }
    if (data.max_apply === "YES" && parseInt(data?.max_value) === 0) {
      toast.error("Max Discount Value 0 can't be accepted");
      return null;
    }
    let constructedBody: ICouponServiceProps = null;
    if (coupon_id && data.is_membership_coupon === "YES" && data.is_exhaustible === "YES") {
      constructedBody = {
        page_step: "details_and_schedule",
        coupon_name: data.coupon_name,
        is_membership_coupon: data.is_membership_coupon === "YES" ? true : false,
        is_exhaustible: data.is_exhaustible === "YES" ? true : false,
        coupon_id: coupon_id,
        coupon_code: data.coupon_code,
        description: data.description ? data.description : null,
        heading: data.heading ? data.heading : null,
        coupon_type: data.coupon_type,
        image_url: data.icon_image ? data.icon_image : null,
        terms_and_conditions: data.terms_and_conditions ? data.terms_and_conditions : null,
        max_apply: data.max_apply,
        max_discount_amount: data.max_apply === "NO" ? null : parseInt(data.max_value),
        start_date_time:
          data.is_membership_coupon === "YES" ? null : `${data.start_date} ${data.start_time_h}:${data.start_time_m}`,
        end_date_time:
          data.coupon_expiry === "NO" || data.is_membership_coupon === "YES"
            ? null
            : `${data.end_date} ${data.end_time_h}:${data.end_time_m}`,
        apply_coupon_changes_to: data.apply_changes,
      };
    } else if (coupon_id) {
      constructedBody = {
        page_step: "details_and_schedule",
        coupon_name: data.coupon_name,
        is_membership_coupon: data.is_membership_coupon === "YES" ? true : false,
        is_exhaustible: data.is_exhaustible === "YES" ? true : false,
        coupon_id: coupon_id,
        coupon_code: data.coupon_code,
        description: data.description ? data.description : null,
        heading: data.heading ? data.heading : null,
        coupon_type: data.coupon_type,
        image_url: data.icon_image ? data.icon_image : null,
        terms_and_conditions: data.terms_and_conditions ? data.terms_and_conditions : null,
        max_apply: data.max_apply,
        max_discount_amount: data.max_apply === "NO" ? null : parseInt(data.max_value),
        minimum_cart_value: parseInt(data?.min_cart_value),
        start_date_time:
          data.is_membership_coupon === "YES" ? null : `${data.start_date} ${data.start_time_h}:${data.start_time_m}`,
        end_date_time:
          data.coupon_expiry === "NO" || data.is_membership_coupon === "YES"
            ? null
            : `${data.end_date} ${data.end_time_h}:${data.end_time_m}`,
      };
    } else {
      constructedBody = {
        page_step: "details_and_schedule",
        coupon_name: data.coupon_name,
        coupon_code: data.coupon_code,
        is_membership_coupon: data.is_membership_coupon === "YES" ? true : false,
        is_exhaustible: data.is_membership_coupon === "NO" ? false : data.is_exhaustible === "YES" ? true : false,
        description: data.description ? data.description : null,
        heading: data.heading ? data.heading : null,
        coupon_type: data.coupon_type,
        image_url: data.icon_image ? data.icon_image : null,
        terms_and_conditions: data.terms_and_conditions ? data.terms_and_conditions : null,
        max_apply: data.max_apply,
        max_discount_amount: data.max_apply === "NO" ? null : parseInt(data.max_value),
        minimum_cart_value: parseInt(data?.min_cart_value),
        start_date_time:
          data.is_membership_coupon === "YES" ? null : `${data.start_date} ${data.start_time_h}:${data.start_time_m}`,
        end_date_time:
          data.coupon_expiry === "NO" || data.is_membership_coupon === "YES"
            ? null
            : `${data.end_date} ${data.end_time_h}:${data.end_time_m}`,
      };
    }
    setIsSubmitting(true);
    if (coupon_id) {
      updateCoupons(constructedBody).then(({ status }) => {
        setIsSubmitting(false);
        if (status) {
          navigation(`/app/coupon-service/coupon-creation?page_step=applicable_conditions&&coupon_id=${coupon_id}`);
        }
      });
    } else {
      couponCreation(constructedBody).then(({ status, message }) => {
        setIsSubmitting(false);
        if (status) {
          navigation(`/app/coupon-service/coupon-creation?page_step=applicable_conditions&&coupon_id=${message}`);
        }
      });
    }
  };

  const handelBackStepper = () => {
    navigation(`/app/coupon-service/coupon-list`);
  };

  useEffect(() => {
    if (couponTypeItems) {
      setCouponTypeData(couponTypeItems);
    }
  }, [couponTypeItems]);

  // useEffect(() => {
  //   const isMembership = formHook.getValues("is_membership_coupon");
  //   if (isMembership === "YES") {
  //     const newCouponType = couponTypeItems.filter((i) => i.title !== "DISCOUNT ON MEMBERSHIP");
  //     setCouponTypeData(newCouponType);
  //   }
  // }, [formHook.watch("is_membership_coupon"), couponTypeItems]);

  useEffect(() => {
    if (coupon_id) {
      fetchCouponDetails(coupon_id);
    }
  }, [coupon_id]);

  useEffect(() => {
    if (couponDetails) {
      setImageFile(couponDetails.image_url);
      formHook.setValue("coupon_name", couponDetails.coupon_name);
      formHook.setValue("coupon_code", couponDetails.coupon_code);
      formHook.setValue("description", couponDetails.description);
      formHook.setValue("is_membership_coupon", couponDetails.is_membership_coupon ? "YES" : "NO");
      formHook.setValue("is_exhaustible", couponDetails.is_exhaustible ? "YES" : "NO");
      formHook.setValue("heading", couponDetails.heading);
      formHook.setValue("coupon_type", couponDetails.coupon_type);
      formHook.setValue("icon_image", couponDetails.image_url);
      formHook.setValue("terms_and_conditions", couponDetails.terms_and_conditions);
      formHook.setValue("max_apply", couponDetails.max_apply);
      formHook.setValue("max_value", couponDetails.max_discount_amount?.toString());
      formHook.setValue("min_cart_value", (couponDetails.minimum_cart_value ?? couponDetails.cart_discount_details?.minimum_cart_value)?.toString());
      if (couponDetails?.start_date_time) {
        const start_date_time = couponDetails?.start_date_time?.split(" ");
        formHook.setValue("start_date", start_date_time[0]);
        formHook.setValue("start_time_h", start_date_time[1]?.split(":")[0]);
        formHook.setValue("start_time_m", start_date_time[1]?.split(":")[1]);
      }
      if (couponDetails?.end_date_time === null) {
        formHook.setValue("coupon_expiry", "NO");
      }
      if (couponDetails?.apply_coupon_changes_to) {
        formHook.setValue("apply_changes", couponDetails?.apply_coupon_changes_to);
      }
      if (couponDetails?.end_date_time) {
        const end_date_time = couponDetails?.end_date_time?.split(" ");
        formHook.setValue("end_date", end_date_time[0]);
        formHook.setValue("end_time_h", end_date_time[1]?.split(":")[0]);
        formHook.setValue("end_time_m", end_date_time[1]?.split(":")[1]);
      }
    }
  }, [couponDetails]);

  useEffect(() => {
    fetchCouponType();
    fetchAllowCoupon();
  }, []);

  useEffect(() => {
    const imageFile = formHook.getValues("image");
    if (imageFile?.length > 0 && imageFile[0]?.size < 100 * 1024 && imageFile[0]?.type !== "text/csv") {
      convertFileToImage(imageFile[0]).then(({ status, message }) => {
        if (status) {
          formHook.setValue("icon_image", message);
          setImageFile(message);
        }
      });
    }
    formHook.setValue("icon_image", null);
    setImageFile(null);
  }, [formHook.watch("image")]);

  return (
    <div className="p-4 bg-white">
      <div className="p-3 py-3">
        <h2 className="text-sm font-bold text-gray-800 tracking-wide uppercase mb-4">Details</h2>
        <form className="w-full mt-2" onSubmit={formHook.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-5 w-full mb-16">
            {/* Row 1: Type */}
            <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12">
                <p className="text-sm font-semibold text-gray-700">
                  Type <span className="text-red-400">*</span>
                </p>
              </div>
              <div className="w-full sm:w-5/12 lg:w-4/12">
                <SearchableSelectMenu
                  errorMessages={[{ message: "", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("coupon_type", item.title);
                      if (RESTRICTED_COUPON_TYPES.includes(item.title)) {
                        formHook.setValue("is_membership_coupon", "NO");
                      } else {
                        formHook.setValue("is_membership_coupon", "YES");
                      }
                    }
                  }}
                  fieldError={formHook.formState.errors.coupon_type}
                  register={formHook.register("coupon_type", { required: true })}
                  selectItems={couponTypeData}
                  placeholder="Select Type"
                  showTooltips={true}
                  disabled={coupon_id ? true : false}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  defaultSelected={couponTypeData?.filter((oc) => oc.title === formHook.watch("coupon_type"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12">
                <p className="text-sm font-semibold text-gray-700">
                  Name <span className="text-red-400">*</span>
                </p>
              </div>
              <div className="w-full sm:w-5/12 lg:w-4/12">
                <Input
                  className="text-xs"
                  placeholder="Enter Name"
                  maxLength={36}
                  disabled={!!coupon_id}
                  register={formHook.register("coupon_name", {
                    required: true,
                    maxLength: 36,
                    ...(forAllAcceptWithOutStartWithDotAndSpace.validations as any),
                  })}
                  fieldError={formHook.formState.errors.coupon_name}
                  errorMessages={[
                    { message: "", type: "required" },
                    { message: "Length should be less than or equal to 36", type: "maxLength" },
                    forAllAcceptWithOutStartWithDotAndSpace.errors,
                  ]}
                />
              </div>
            </div>

            {/* Row 3: Coupon Code */}
            <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12">
                <p className="text-sm font-semibold text-gray-700">
                  Coupon Code <span className="text-red-400">*</span>
                </p>
              </div>
              <div className="w-full sm:w-5/12 lg:w-4/12">
                <Input
                  className="text-xs"
                  placeholder="Enter Code"
                  maxLength={36}
                  disabled={!!coupon_id}
                  register={formHook.register("coupon_code", {
                    required: true,
                    maxLength: 36,
                    ...(forAllAcceptWithOutStartWithDotAndSpace.validations as any),
                  })}
                  fieldError={formHook.formState.errors.coupon_code}
                  errorMessages={[
                    { message: "", type: "required" },
                    { message: "Length should be less than or equal to 36", type: "maxLength" },
                    forAllAcceptWithOutStartWithDotAndSpace.errors,
                  ]}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12">
                <p className="text-sm font-semibold text-gray-700">
                  Membership Coupon <span className="text-red-400">*</span>
                </p>
              </div>
              <div className="w-full sm:w-5/12 lg:w-4/12">
                <NonSearchableSelectMenu
                  errorMessages={[{ message: "", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("is_membership_coupon", item.title);
                    }
                  }}
                  fieldError={formHook.formState.errors.is_membership_coupon}
                  register={formHook.register("is_membership_coupon", { required: true })}
                  selectItems={booleanArray}
                  placeholder="Select"
                  showTooltips={false}
                  disabled={isMembershipDisabled}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={booleanArray?.filter((oc) => oc.title === formHook.watch("is_membership_coupon"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-28"
                />{" "}
              </div>
            </div>
            {formHook.watch("is_membership_coupon") === "YES" && (
              <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                <div className="w-full sm:w-2/12">
                  <p className="text-sm font-semibold text-gray-700">
                    Is Exhaustible <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-5/12 lg:w-4/12">
                  <NonSearchableSelectMenu
                    errorMessages={[{ message: "", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("is_exhaustible", item.title);
                      }
                    }}
                    fieldError={formHook.formState.errors.is_exhaustible}
                    register={formHook.register("is_exhaustible", { required: true })}
                    selectItems={booleanArray}
                    placeholder="Select"
                    showTooltips={false}
                    disabled={coupon_id ? true : false}
                    showTypedErrors={false}
                    showDropdownIcon={true}
                    defaultSelected={booleanArray?.filter((oc) => oc.title === formHook.watch("is_exhaustible"))[0]}
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-28"
                  />{" "}
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-start py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12 sm:pt-2">
                <p className="text-sm font-semibold text-gray-700">
                  Heading <span className="text-red-400">*</span>
                </p>
              </div>
              <div className="w-full sm:w-5/12 lg:w-4/12">
                <Input
                  className="text-xs"
                  placeholder="Enter Heading"
                  disabled={!!coupon_id}
                  register={formHook.register("heading", {
                    required: true,
                    ...(forAllAcceptWithOutStartWithDotAndSpace.validations as any),
                  })}
                  fieldError={formHook.formState.errors.heading}
                  errorMessages={[{ message: "", type: "required" }, forAllAcceptWithOutStartWithDotAndSpace.errors]}
                />
                <p className="text-[12px] text-gray-400 mt-1.5">
                  For eg. ; Unlimited OPD Consultation @ 199” or “5 Free Spa Baths
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-start py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12 sm:pt-2">
                <p className="text-sm font-semibold text-gray-700">Description</p>
              </div>
              <div className="w-full sm:w-8/12 lg:w-6/12">
                <textarea
                  value={formHook.watch("description") || ""}
                  className="text-xs w-full h-24 p-2.5 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all duration-200"
                  placeholder="Add Description....."
                  disabled={!!coupon_id}
                  onChange={(e) => formHook.setValue("description", e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-start py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12 sm:pt-2">
                <p className="text-sm font-semibold text-gray-700">
                  Icon Image {isQCCoupon && <span className="text-red-400">*</span>}
                  <span className="text-[12px] block mt-0.5 text-gray-400 font-normal normal-case">(image should be 1:1)</span>
                </p>
              </div>
              <div className="w-full sm:w-8/12 lg:w-6/12 mb-1">
                {imageFile ? (
                  <div className="flex items-center gap-3 my-1">
                    <div className="relative w-20 h-20 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                      <XMarkIcon
                        className="absolute top-1 right-1 w-4 h-4 cursor-pointer text-white bg-black bg-opacity-50 rounded-full p-1"
                        onClick={() => {
                          setImageFile(null);
                          formHook.setValue("icon_image", null);
                          formHook.resetField("image");
                        }}
                      />
                      <img src={imageFile} alt="aa" className="w-full h-full object-cover" />
                    </div>
                    <p
                      className="flex items-center gap-2 text-[#0068FF] cursor-pointer text-xs font-semibold"
                      onClick={() => {
                        (setImageFile(null), formHook.resetField("image"));
                        formHook.setValue("icon_image", null);
                      }}
                    >
                      <RxUpload className="w-4 h-4" />
                      Replace image
                    </p>
                  </div>
                ) : (
                  <InputWithFileUpload
                    variant="DRAG_AND_DROP"
                    fieldError={formHook.formState.errors?.image ?? {}}
                    register={formHook?.register("image", {
                      required: false,
                      onChange: () => formHook.trigger(`image`),
                      validate: {
                        maxSize: (files: FileList) => {
                          return (
                            Array.from(files).every((file) => file.size < 100 * 1024) ||
                            "Each file must be smaller than 100KB and Aspect ratio of the image should be 1:1"
                          );
                        },
                        fileType: (files: FileList) => {
                          const validTypes = ["image/png", "image/jpeg"];
                          return (
                            Array.from(files).every((file) => validTypes.includes(file.type)) || "Invalid file type"
                          );
                        },
                      },
                    })}
                    type="file"
                    onClear={() => {
                      formHook.resetField("image");
                    }}
                    className="border-slate-400 rounded-md w-[300px] h-[200px]"
                    id={"image"}
                    errorMessages={[{ type: "required", message: "" }]}
                    dragAndDropDescription={{
                      allowedFormats: ["JPEG", "PNG"],
                      maxFileSize: "100KB Aspect ratio of the image should be 1:1",
                    }}
                  />
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-start py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12 sm:pt-2">
                <p className="text-sm font-semibold text-gray-700">
                  Terms & Conditions {isQCCoupon && <span className="text-red-400">*</span>}
                </p>
              </div>
              <div className="w-full sm:w-8/12 lg:w-6/12">
                <textarea
                  value={formHook.watch("terms_and_conditions") || ""}
                  className="text-xs w-full h-24 p-2.5 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all duration-200"
                  placeholder="Enter Terms & Conditions..."
                  disabled={!!coupon_id}
                  onChange={(e) => formHook.setValue("terms_and_conditions", e.target.value)}
                />
              </div>
            </div>

            {/* Row 10: Minimum Cart Value */}
            {isQCCoupon && (
              <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                <div className="w-full sm:w-2/12">
                  <p className="text-sm font-semibold text-gray-700">Minimum Cart Value</p>
                </div>
                <div className="w-full sm:w-5/12 lg:w-4/12">
                  <Input
                    className="text-xs"
                    placeholder="Enter Minimum Cart Value"
                    register={formHook.register("min_cart_value", {
                      required: true,
                      ...(forOnlyPercentageAndNumberRegex.validations as any),
                    })}
                    fieldError={formHook.formState.errors.min_cart_value}
                    errorMessages={[{ message: "", type: "required" }, forOnlyPercentageAndNumberRegex.errors]}
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
              <div className="w-full sm:w-2/12">
                <p className="text-sm font-semibold text-gray-700">
                  Add Max Discount Value <span className="text-red-400">*</span>
                </p>
              </div>
              <div className="w-full sm:w-5/12 lg:w-4/12">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("max_apply", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.max_apply}
                      register={formHook.register("max_apply", { required: true })}
                      selectItems={booleanArray}
                      placeholder="Select"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      defaultSelected={booleanArray?.filter((oc) => oc.title === formHook.watch("max_apply"))[0]}
                      listBoxClassName="w-36"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                  {formHook.watch("max_apply") === "YES" && (
                    <div className="col-span-2">
                      <Input
                        className="text-xs"
                        placeholder="Enter Value"
                        register={formHook.register("max_value", {
                          required: true,
                          max: 9999,
                          ...(forOnlyPercentageAndNumberRegex.validations as any),
                        })}
                        fieldError={formHook.formState.errors.max_value}
                        errorMessages={[
                          { message: "", type: "required" },
                          forOnlyPercentageAndNumberRegex.errors,
                          { message: "Maximum value allowed is 9999", type: "max" },
                        ]}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {formHook.watch("is_exhaustible") === "YES" &&
              coupon_id &&
              formHook.watch("is_membership_coupon") === "YES" && (
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">
                      Upon edit apply changes to <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-5/12 lg:w-4/12">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("apply_changes", item.id);
                        }
                      }}
                      fieldError={formHook.formState.errors.apply_changes}
                      register={formHook.register("apply_changes", { required: true })}
                      selectItems={allowCouponItem}
                      placeholder="Select"
                      showDropdownIcon={true}
                      defaultSelected={allowCouponItem?.filter((oc) => oc.id === formHook.watch("apply_changes"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
              )}

            {formHook.watch("is_membership_coupon") === "NO" && (
              <div className="mt-6 border-t border-gray-100 pt-6">
                <h3 className="text-sm font-bold text-gray-800 tracking-wide mb-4 uppercase">Schedule</h3>

                {/* Start Date & Time */}
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0 border-b border-gray-50 pb-4">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">
                      Start Date & Time <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-8/12 lg:w-6/12">
                    <div className="flex flex-wrap items-center gap-3 w-full">
                      <div className="w-40 flex-shrink-0">
                        <PWLCalendarComponent
                          showFutureYears
                          allowPastDaySelection={false}
                          errorMessages={[{ message: "Date is required", type: "required" }]}
                          register={formHook.register("start_date", {
                            required: true,
                          })}
                          disableDatesBeforeDate={format(new Date(Date.now()), stringConstants?.fieldDOBFormat)}
                          fieldError={formHook.formState.errors.start_date}
                          onSelectDate={(date) => {
                            formHook.setValue("start_date", format(date, stringConstants?.fieldDOBFormat));
                          }}
                          defaultSelectedDate={formHook.getValues("start_date")}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 flex-shrink-0">
                          <NonSearchableSelectMenu
                            errorMessages={[{ message: "", type: "required" }]}
                            onSelectItem={(item) => {
                              if (item) {
                                formHook.setValue("start_time_h", item.title);
                              }
                            }}
                            fieldError={formHook.formState.errors.start_time_h}
                            register={formHook.register("start_time_h", { required: true })}
                            selectItems={startTimeItem}
                            placeholder="HH"
                            showTooltips={false}
                            showTypedErrors={false}
                            showDropdownIcon={true}
                            defaultSelected={startTimeItem?.filter((oc) => oc.title === formHook.watch("start_time_h"))[0]}
                            listBoxClassName="w-full"
                            className="text-gray-800"
                          />
                        </div>
                        <div className="w-24 flex-shrink-0">
                          <NonSearchableSelectMenu
                            errorMessages={[{ message: "startTimeMinuteItem is required", type: "required" }]}
                            onSelectItem={(item) => {
                              if (item) {
                                formHook.setValue("start_time_m", item.title);
                              }
                            }}
                            fieldError={formHook.formState.errors.start_time_m}
                            register={formHook.register("start_time_m", { required: true })}
                            selectItems={startTimeMinuteItem}
                            placeholder="MM"
                            showTooltips={false}
                            showTypedErrors={false}
                            showDropdownIcon={true}
                            defaultSelected={
                              startTimeMinuteItem?.filter((oc) => oc.title === formHook.watch("start_time_m"))[0]
                            }
                            listBoxClassName="w-full"
                            className="text-gray-800"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expiry Date & Time */}
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0 pt-4">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">
                      Add Expiry Date & Time <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-8/12 lg:w-6/12">
                    <div className="flex flex-wrap items-center gap-3 w-full">
                      <div className="w-32 flex-shrink-0">
                        <SearchableSelectMenu
                          errorMessages={[{ message: "Country is required", type: "required" }]}
                          onSelectItem={(item) => {
                            if (item) {
                              formHook.setValue("coupon_expiry", item.title);
                            }
                          }}
                          fieldError={formHook.formState.errors.coupon_expiry}
                          register={formHook.register("coupon_expiry", { required: true })}
                          selectItems={booleanArray}
                          placeholder="Select Country"
                          showTooltips={true}
                          showTypedErrors={true}
                          showDropdownIcon={true}
                          defaultSelected={
                            booleanArray?.filter((oc) => oc.title === formHook.watch("coupon_expiry"))[0]
                          }
                          listBoxClassName="w-36"
                          className="text-gray-800"
                          containerClassName="w-full"
                          disabled={isQCCoupon}
                        />
                      </div>
                      {formHook.watch("coupon_expiry") === "YES" && (
                        <div className="w-40 flex-shrink-0">
                          <PWLCalendarComponent
                            showFutureYears
                            allowPastDaySelection={false}
                            errorMessages={[{ message: "", type: "required" }]}
                            register={formHook.register("end_date", {
                              required: true,
                            })}
                            disableDatesBeforeDate={formHook.watch("start_date")}
                            fieldError={formHook.formState.errors.end_date}
                            onSelectDate={(date) => {
                              formHook.setValue("end_date", format(date, stringConstants?.fieldDOBFormat));
                            }}
                            defaultSelectedDate={formHook.getValues("end_date")}
                            className="w-full"
                          />
                        </div>
                      )}
                      {formHook.watch("coupon_expiry") === "YES" && (
                        <div className="flex items-center gap-2">
                          <div className="w-24">
                            <NonSearchableSelectMenu
                              errorMessages={[{ message: "", type: "required" }]}
                              onSelectItem={(item) => {
                                if (item) {
                                  formHook.setValue("end_time_h", item.title);
                                }
                              }}
                              fieldError={formHook.formState.errors.end_time_h}
                              register={formHook.register("end_time_h", { required: true })}
                              selectItems={startTimeItem}
                              placeholder="HH"
                              showTooltips={false}
                              showTypedErrors={true}
                              showDropdownIcon={true}
                              defaultSelected={
                                startTimeItem?.filter((oc) => oc.title === formHook.watch("end_time_h"))[0]
                              }
                              listBoxClassName="w-full"
                              className="text-gray-800"
                              containerClassName="w-full"
                            />
                          </div>
                          <div className="w-24">
                            <NonSearchableSelectMenu
                              errorMessages={[{ message: "", type: "required" }]}
                              onSelectItem={(item) => {
                                if (item) {
                                  formHook.setValue("end_time_m", item.title);
                                }
                              }}
                              fieldError={formHook.formState.errors.end_time_m}
                              register={formHook.register("end_time_m", { required: true })}
                              selectItems={startTimeMinuteItem}
                              placeholder="MM"
                              showTooltips={false}
                              showTypedErrors={true}
                              showDropdownIcon={true}
                              defaultSelected={
                                startTimeMinuteItem?.filter((oc) => oc.title === formHook.watch("end_time_m"))[0]
                              }
                              listBoxClassName="w-full"
                              className="text-gray-800"
                              containerClassName="w-full"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="right-5 bottom-0 fixed flex justify-end items-center gap-4 border-t-2 w-full p-3 bg-white">
            <button
              type="button"
              onClick={() => handelBackStepper()}
              className={`${SECONDARY_BUTTON} mt-3 w-full sm:mt-0 sm:w-auto`}
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`${PRIMARY_BUTTON} mt-3 w-full sm:mt-0 sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {isSubmitting && (
                <svg
                  className="animate-spin mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {isSubmitting ? "Saving..." : "Save & Next"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default CouponDetailsComponent;
